import * as React from 'react';
import { observer } from 'src/utils/mobx-react';
import { slug } from 'src/utils/deburr';
import { SelectionNewVersion } from 'src/domains/sportsbook/webview/components/Selection';
import { EventSummaryConnect } from 'src/domains/sportsbook/webview/components/containers/eventSummary/EventSummary';
import {
    EventRowMeta,
    EventRowNameLink,
    EventRowWrapper,
} from 'src/domains/sportsbook/webview/components/eventRow/EventRow.style';
import { MarketModel } from 'src_common/common/websocket2/models/MarketModel';
import {
    EventRowHeader,
    EventRowMarkets,
    EventRowSelections,
    EventRowSelectionsElement,
    EventRowMetaTime,
    EventRowParticipantsAndScore,
    EventRowParticipants,
} from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventRowMobx.style';
import { EventListGroupEventItemType } from 'src_common/common/websocket2/modelsApi/EventsCollectionQuery';
import { getMarketForDisplayUnified } from 'src/domains/sportsbook/webview/components/sportEventsListNew/getEventAndMarket';
import { Participant } from 'src/domains/sportsbook/webview/components/events/row/Participant';
import { EventStatusAndDate } from 'src/domains/sportsbook/webview/components/events/row/StatusAndDate';
import { AppState, useAppStateContext } from 'src/appState/AppState';
import { UnifiedSportEventsListState } from 'src/domains/sportsbook/state/eventsListState/unifiedSportEventsListState/UnifiedSportEventsListState';
import { CompetitionId } from 'src_common/common/websocket2/id/WebsocketId';
import {
    getLastSelections,
    shouldUseExpandedSelectionLayout,
} from 'src/domains/sportsbook/state/eventsListState/unifiedSportEventsListState/UnifiedEventsListMarkets';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';
import { EventRow6PackState } from 'src/domains/sportsbook/webview/components/eventsList/eventRow6Pack/EventRow6Pack.state';
import { EventId } from 'src_common/common/websocket2/id/WebsocketId';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { GolfSelections } from 'src/domains/sportsbook/webview/components/sportEventsListNew/SportCompetitionsList/golf/GolfSelections';
import { ChevronWrapper } from 'src/ui/components/collapse/sports/05/Collapse.style';
import { RowLinkLabelWrapper } from 'src/ui/components/table/sports/row/link/TableSportsRowLink.style';
import {
    isLineMarket,
    isSignedLineMarket,
    resolveSignedLineLabel,
} from 'src/domains/sportsbook/state/eventsListState/unifiedSportEventsListState/lineMarkets';
import { isOutrightEventItem } from 'src/domains/sportsbook/utils/isOutrightEvent';

export const getMarket = (
    appState: AppState,
    smallEvent: EventListGroupEventItemType,
    eventsListState: UnifiedSportEventsListState
): MarketModel | 'loading' | null => {
    const eventModel = smallEvent.id2.getEventModel();

    if (eventModel === null) {
        return null;
    }

    return getMarketForDisplayUnified(appState, eventModel, eventsListState);
};

interface EventRowUnifiedRenderSelectionsPropsType {
    smallEvent: EventListGroupEventItemType;
    eventsListState: UnifiedSportEventsListState;
    selectionsHeaders?: Array<string>;
    variant: DefaultComponentVariantType | null;
    isExpandedSelectionLayout: boolean;
}

const EventRowUnifiedRenderSelections = observer(
    'EventRowUnifiedRenderSelections',
    (props: EventRowUnifiedRenderSelectionsPropsType): JSX.Element | null => {
        const { variant, smallEvent, eventsListState, selectionsHeaders = [], isExpandedSelectionLayout } = props;
        const appState = useAppStateContext();
        const { unifiedComponentConfig } = appState;
        const market = getMarket(appState, smallEvent, eventsListState);

        if (market === null || market === 'loading') {
            return null;
        }

        const selectionsRaw = selectionsHeaders.length > 0 ? selectionsHeaders : getLastSelections(market.template.id);
        const availableIds = new Set(
            market.selections
                .filter((selection) => selection.display)
                .map((selection) => selection.selectionIdentifiers)
        );
        const isTwoWay =
            availableIds.has('H') === true &&
            availableIds.has('A') === true &&
            availableIds.has('D') === false &&
            availableIds.has('T') === false;

        const headerExpectsDraw = selectionsRaw.includes('D') || selectionsRaw.includes('T');
        const selections =
            isTwoWay && headerExpectsDraw === false
                ? selectionsRaw.filter((id) => id !== 'D' && id !== 'T')
                : selectionsRaw;
        const marketTemplateId = eventsListState.filter_marketTemplateId;
        const shouldShowLine = market.line !== null && isLineMarket(marketTemplateId);
        const showLine = shouldShowLine && unifiedComponentConfig.unifiedSportEventsTable;
        const isSignedLine = isSignedLineMarket(market.templateId, market.template.name);

        return selections.length > 0 ? (
            <EventRowSelections
                variant={variant}
                isExpandedSelectionLayout={isExpandedSelectionLayout}
            >
                {selections.map((id) => {
                    const selection = market.selections.find(
                        (s) => market.displayed && s.display && s.selectionIdentifiers === id
                    );

                    const lineLabel = showLine
                        ? isSignedLine
                            ? resolveSignedLineLabel(id, market.line) ?? null
                            : market.line?.toString() ?? null
                        : null;

                    return (
                        <EventRowSelectionsElement
                            className='unified'
                            key={id}
                        >
                            <SelectionNewVersion
                                selectionId={selection?.id2}
                                sp={false}
                                borderLeft={true}
                                borderRight={true}
                                lineLabel={lineLabel}
                                isExpandedLayout={isExpandedSelectionLayout}
                            />
                        </EventRowSelectionsElement>
                    );
                })}
            </EventRowSelections>
        ) : null;
    }
);

interface EventRowUnifiedPropsType {
    smallEvent: EventListGroupEventItemType;
    competitionId: CompetitionId | null;
    sportId: string;
    selectionsHeaders: Array<string>;
    /**
     * Page-level list state (sport-wide or competition route). Prefer this over looking up
     * by the event's competition id — per-competition instances always have competitionId set
     * and would incorrectly keep Matches market filters on Featured.
     */
    eventsListState?: UnifiedSportEventsListState;
    rowStateConstructor?: (new (eventId: EventId, language: LanguagesState) => EventRow6PackState) | null;
}

export const EventRowUnified = observer('EventRowUnified', (props: EventRowUnifiedPropsType) => {
    const {
        smallEvent,
        competitionId,
        sportId,
        selectionsHeaders,
        eventsListState: eventsListStateProp,
        rowStateConstructor = null,
    } = props;

    const appState = useAppStateContext();
    const { appSportsBookState, unifiedComponentConfig, languagesState } = appState;
    // TODO: [Genius] To remove this check and leave only config.componentVariants.eventRowVariant when GLOB-37 is fully live
    const {
        componentVariants: { eventRowVariant },
    } = unifiedComponentConfig;

    const eventsListState =
        eventsListStateProp ?? appSportsBookState.unifiedSportEventsListState.get(sportId).get(competitionId);
    const isOutrights = isOutrightEventItem(smallEvent);
    const isExpandedSelectionLayout = shouldUseExpandedSelectionLayout(sportId);
    const isColumnSelectionLayout = sportId === 'golf';

    const layoutOptions = {
        isColumnSelectionLayout,
    };

    const filterMarketsByPopularTag = false;
    const rowState = rowStateConstructor ? new rowStateConstructor(smallEvent.id2, languagesState) : null;

    return (
        <>
            <EventRowWrapper
                variant={eventRowVariant}
                isOutrights={isOutrights}
                layoutOptions={layoutOptions}
                data-testid='event-row-unified'
            >
                {isOutrights ? (
                    <EventRowMarkets>
                        <EventSummaryConnect
                            id={appSportsBookState.models.id.getEventId(smallEvent.id)}
                            filterMarketsByPopularTag={filterMarketsByPopularTag}
                        />
                    </EventRowMarkets>
                ) : (
                    <>
                        <EventRowHeader variant={eventRowVariant}>
                            <EventRowNameLink
                                to={{ name: 'event', id: smallEvent.id, slug: slug(smallEvent.name) }}
                                dataTest='EventRowUnifiedNameLink'
                                variant={eventRowVariant}
                                layoutOptions={layoutOptions}
                            >
                                <RowLinkLabelWrapper>
                                    <EventRowParticipantsAndScore>
                                        <EventRowParticipants>
                                            <Participant
                                                smallEvent={smallEvent}
                                                state={rowState}
                                            />
                                        </EventRowParticipants>
                                    </EventRowParticipantsAndScore>
                                    <EventRowMeta variant={eventRowVariant}>
                                        <EventRowMetaTime variant={eventRowVariant}>
                                            <EventStatusAndDate
                                                dateFormat='onlyToday'
                                                smallEvent={smallEvent}
                                            />
                                        </EventRowMetaTime>
                                    </EventRowMeta>
                                </RowLinkLabelWrapper>

                                {isColumnSelectionLayout ? (
                                    <ChevronWrapper
                                        size={20}
                                        position='right'
                                    />
                                ) : null}
                            </EventRowNameLink>
                        </EventRowHeader>
                        <EventRowUnifiedRenderSelections
                            smallEvent={smallEvent}
                            eventsListState={eventsListState}
                            selectionsHeaders={selectionsHeaders}
                            variant={eventRowVariant}
                            isExpandedSelectionLayout={isExpandedSelectionLayout}
                        />
                    </>
                )}
            </EventRowWrapper>
            {isColumnSelectionLayout && !isOutrights ? <GolfSelections smallEventId={smallEvent.id2} /> : null}
        </>
    );
});
